import { Helmet } from "react-helmet-async";
import { Navigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import HowItWorksSection from "@/components/HowItWorksSection";
import BenefitsSection from "@/components/BenefitsSection";
import SafetySection from "@/components/SafetySection";
import WhoThisIsForSection from "@/components/WhoThisIsForSection";
import WhyElaraSection from "@/components/WhyElaraSection";
import PeptidesSection from "@/components/PeptidesSection";
import PricingSection from "@/components/PricingSection";
import FinalCTASection from "@/components/FinalCTASection";
import Footer from "@/components/Footer";
import { useReferralCapture } from "@/hooks/useReferral";
import { useAuth } from "@/contexts/AuthContext";
import { APP_URL } from "@/lib/app-config";

const buildJsonLd = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${APP_URL}/#organization`,
      name: "Elara Health",
      url: APP_URL,
      logo: `${APP_URL}/elara-logo.png`,
      description:
        "Clinician-guided, personalized metabolic and longevity care platform delivering prescription treatments to your door.",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Long Island",
        addressRegion: "NY",
        addressCountry: "US",
      },
      sameAs: [],
    },
    {
      "@type": "MedicalBusiness",
      "@id": `${APP_URL}/#medicalbusiness`,
      name: "Elara Health",
      url: APP_URL,
      description:
        "Personalized metabolic and longevity optimization through clinician-guided telehealth consultations, prescription management, and ongoing provider support.",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Long Island",
        addressRegion: "NY",
        addressCountry: "US",
      },
      medicalSpecialty: "Endocrinology",
      priceRange: "$349–$649/month",
      isAcceptingNewPatients: true,
      availableService: [
        {
          "@type": "MedicalTherapy",
          name: "Metabolic Support",
          description:
            "Supports weight management and metabolic function through personalized clinical care.",
        },
        {
          "@type": "MedicalTherapy",
          name: "Energy & Cellular Health",
          description:
            "Supports energy levels and overall vitality based on symptoms and health profile.",
        },
        {
          "@type": "MedicalTherapy",
          name: "Recovery & Performance",
          description:
            "Supports physical recovery and resilience based on activity level and recovery needs.",
        },
        {
          "@type": "MedicalTherapy",
          name: "Hormonal Optimization",
          description:
            "Supports endocrine balance and overall wellbeing with provider evaluation and monitoring.",
        },
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${APP_URL}/#website`,
      url: APP_URL,
      name: "Elara Health",
      publisher: { "@id": `${APP_URL}/#organization` },
    },
  ],
});

const Index = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  useReferralCapture();

  if (loading) return null;
  if (user && !location.hash) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Elara Health | Clinician-Guided Metabolic & Longevity Care</title>
        <meta
          name="description"
          content="Personalized treatment plans designed to support metabolism, recovery, and overall health. Delivered to your door. Licensed U.S. clinicians. Starting at $349/month."
        />
        <link rel="canonical" href={`${APP_URL}/`} />
        <script type="application/ld+json">{JSON.stringify(buildJsonLd())}</script>
      </Helmet>
      <Navbar />
      <HeroSection />
      <HowItWorksSection />
      <BenefitsSection />
      <SafetySection />
      <WhoThisIsForSection />
      <WhyElaraSection />
      <PeptidesSection />
      <PricingSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
};

export default Index;
